/**
 * `<StatusChip/>` + `<StatusDot/>` — the factual state pill / dot.
 *
 * Issue #759 (Phase 2). One small component renders a node's state as a
 * pill ("● Done", "◐ In progress", "○ Not started", "⊘/⚠ Blocked") with the
 * §A6/§A9 colours. Calm amber for "blocked" — never alarm red. Display-only;
 * carries no number about a person.
 *
 * Editorial revamp (#1043, Wave 1): theme-aware. The style comes from
 * `theme.stateStyle(state)` instead of the static `taskStateStyle` import, so
 * the chip recolours editorially (warm tints, ⚠ amber, distinct dot vs
 * chip-text colour) when the team's `task_editorial_ui` flag is on, and stays
 * byte-identical legacy when off (legacy `stateStyle` has no `dot`, so the dot
 * falls back to `fg` — exactly today's behaviour). Used inside the canvas
 * cluster, always under a `MapThemeProvider`; a standalone render (unit test)
 * gets the legacy default from the context.
 */

import { useMapTheme } from "../theme/MapThemeContext";
import type { TaskState } from "../tree/state";

export function StatusChip({ state }: { state: TaskState }) {
  const s = useMapTheme().stateStyle(state);
  return (
    <span
      data-testid={`status-chip-${state}`}
      className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium leading-none"
      style={{ backgroundColor: s.chipBg, color: s.fg }}
    >
      <span aria-hidden>{s.glyph}</span>
      {s.label}
    </span>
  );
}

/** A bare state dot (used in the outline rail before each row). */
export function StatusDot({ state }: { state: TaskState }) {
  const s = useMapTheme().stateStyle(state);
  return (
    <span
      aria-hidden
      data-testid={`status-dot-${state}`}
      className="inline-block text-[10px] leading-none"
      // The standalone glyph uses the saturated `dot` colour when the theme
      // provides one (editorial); legacy omits `dot`, so it falls back to `fg`
      // — the dot stays exactly today's colour.
      style={{ color: s.dot ?? s.fg }}
    >
      {s.glyph}
    </span>
  );
}
